import { ApolloClient, InMemoryCache, NormalizedCacheObject } from '@apollo/client'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import merge from 'lodash/merge'
import getConfig from 'next/config'
import { Cookies } from 'react-cookie'

import fragments from '@Generated/fragments'

let apolloClient: ApolloClient<NormalizedCacheObject>

export interface Config {
  locale?: string
  previewEnvironment?: string
}

const createApolloClient = (config: Config = {}): ApolloClient<NormalizedCacheObject> => {
  const { publicRuntimeConfig: { graphqlEndpoint } } = getConfig()

  const link = new BatchHttpLink({
    credentials: 'include',
    uri: (operation) => {
      const { domainProxy, userSpecific } = operation.getContext()

      const graphqlEndpointURL = new URL(domainProxy ? `${window.location.protocol}//${window.location.host}/graph` : graphqlEndpoint)

      if (userSpecific) {
        const cookies = new Cookies()
        const userID = cookies.get('th_uid')

        if (userID) {
          graphqlEndpointURL.searchParams.set('user_id', userID)
        }
      }

      if (config.locale) {
        graphqlEndpointURL.searchParams.set('locale', config.locale)
      }

      if (config.previewEnvironment) {
        graphqlEndpointURL.searchParams.set('preview_environment', config.previewEnvironment)
      }

      return graphqlEndpointURL.toString()
    },
  })

  return new ApolloClient({
    cache: new InMemoryCache({
      possibleTypes: fragments.possibleTypes,
      typePolicies: {
        Block: {
          keyFields: ['type', 'id'],
        },
        Page: {
          keyFields: ['type', 'id'],
        },
        // TODO: This should be removed, hack to bypass referenceKey being overwritten globally
        RTEField: {
          fields: {
            references: {
              merge() {
              },
            },
          },
        },
      },
    }),
    link,
    ssrMode: typeof window === 'undefined',
  })
}

export const initializeApollo = (
  initialState: NormalizedCacheObject = {},
  config: Config = {},
): ApolloClient<NormalizedCacheObject> => {
  const client = apolloClient || createApolloClient(config)

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = client.extract()

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = merge(initialState, existingCache)

    // Restore the cache with the merged data
    client.cache.restore(data)
  }

  if (typeof window === 'undefined') {
    return client
  }

  if (!apolloClient) {
    apolloClient = client
  }

  return client
}
